import { useAuth0 } from '@auth0/auth0-react'
import { Navigate } from 'react-router-dom'
import { useGlobalState } from '@/stores/globalState'
import { hasKnownAccount } from '@/lib/inviteCode'
import { InviteWall } from './InviteWall'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth0()
  const user = useGlobalState((s) => s.user)
  const inviteRequired = useGlobalState((s) => s.inviteRequired)

  // Auth0 still initialising (incl. processing the login callback at "/") —
  // show nothing (avoids flash / premature redirect).
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-primary" />
      </div>
    )
  }

  // Not authenticated. A browser that has completed a sync before gets the
  // returning-parent sign-in ("Good to see you"); a brand-new visitor gets
  // the invite-gated sign-up front door. Neither goes straight to Auth0's
  // hosted page.
  if (!isAuthenticated) {
    return <Navigate to={hasKnownAccount() ? '/auth/login' : '/auth/signup'} replace />
  }

  // Auth0-authenticated but the backend refused to create the account
  // without an invite (403 INVITE_REQUIRED) — ask for the code post-auth.
  if (!user && inviteRequired) {
    return <InviteWall />
  }

  // Auth0 authenticated but profile not yet synced — show loading
  // (ProfileSync in App.tsx will populate user shortly)
  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-primary" />
          <p className="text-sm text-muted-foreground">Setting up your account…</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
